import { Objective, VideoObjectives } from "./objectives-store";
import { ObjectiveProgress } from "./progress-store";
import { UserVideo, VideoStatus } from "./library-store";

// ─── Wire types ────────────────────────────────────────────────────────────

export interface ApiVideoContent {
  youtubeVideoId: string;
  title: string | null;
  channel: string | null;
  durationSec: number | null;
  status: "transcribing" | "quota-exceeded" | "ready";
  summary: string | null;
  instrument: VideoObjectives["instrument"] | null;
  skillCategory: VideoObjectives["skillCategory"] | null;
  difficulty: string | null;
  techniques: string[];
  freeTags: string[];
  objectives: Objective[];
  extractedAt: string | null;
  extractorVersion: string | null;
  transcriptSource: "captions" | "whisper" | null;
  quotaExceededAt?: string | null;
}

interface ApiUserVideo {
  youtubeVideoId: string;
  title: string;
  channel: string;
  thumbnailUrl: string;
  savedAt: number;
  status: VideoStatus;
  // M2.4: skill tags projected from video_content. Null/empty until the
  // video has been transcribed.
  skillCategory?: string | null;
  difficulty?: string | null;
  techniques?: string[];
}

interface ApiObjective extends Objective {
  isCustom: boolean;
}

interface ApiDetailResponse {
  video: ApiUserVideo | null;
  content: ApiVideoContent | null;
  objectives: ApiObjective[];
  progress: Record<string, ObjectiveProgress>;
  bookmarks: ApiBookmark[];
}

export interface ApiBookmark {
  id: string;
  timestampSec: number;
  label: string;
  createdAt: string | null;
  updatedAt: string | null;
}

interface TranscribeResponse {
  video: ApiVideoContent;
  cacheHit?: boolean;
}

export interface XpBreakdown {
  objectives: number;
  videosComplete: number;
  firstOfDayBonuses: number;
  bookmarks: number;
}

export interface WeeklyRecap {
  objectivesCheckedThisWeek: number;
  videosCompletedThisWeek: number;
  firstOfDayThisWeek: number;
  bookmarksThisWeek: number;
  bookmarkXpThisWeek: number;
  xpThisWeek: number;
}

export interface CurrentUserProfile {
  user: {
    userId: string;
    email: string | null;
    givenName: string | null;
    familyName: string | null;
    displayName: string | null;
    createdAt: string | null;
  };
  stats: {
    xp: number;
    xpPerObjective: number;
    xpPerVideoComplete?: number;
    xpPerFirstActionOfDay?: number;
    xpPerBookmark?: number;
    xpBreakdown?: XpBreakdown;
    completedObjectives: number;
    completedVideos?: number;
    currentStreak: number;
    longestStreak: number;
    lastPracticeDate: string | null;
    practiceTimeZone: string;
    // Weekly streak (M2.4b)
    weeklyStreak?: number;
    longestWeeklyStreak?: number;
    currentWeekStartDate?: string;
    currentWeekPracticeDays?: string[];
    weekTarget?: number;
    // Weekly recap (M2.4c)
    weeklyRecap?: WeeklyRecap;
  };
}

export interface YouTubeSearchResult {
  youtubeVideoId: string;
  title: string;
  channel: string;
  thumbnailUrl: string;
  durationSec: number | null;
}

// ─── Config ────────────────────────────────────────────────────────────────

const rawApiUrl = import.meta.env.VITE_API_URL as string | undefined;
export const apiUrl = rawApiUrl?.replace(/\/$/, "") ?? "";
export const isApiConfigured = apiUrl.length > 0;

// ─── Core fetch ────────────────────────────────────────────────────────────

interface RequestOptions {
  method?: string;
  body?: unknown;
}

async function apiRequest<T>(
  path: string,
  token: string,
  opts: RequestOptions = {},
): Promise<T> {
  if (!apiUrl) throw new Error("VITE_API_URL is not configured.");
  const init: RequestInit = {
    method: opts.method ?? "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  };
  if (opts.body !== undefined) init.body = JSON.stringify(opts.body);

  const res = await fetch(`${apiUrl}${path}`, init);
  const text = await res.text();
  let data: unknown = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = null;
    }
  }
  if (!res.ok) {
    const message =
      data && typeof data === "object" && data !== null && "error" in data
        ? String((data as { error: unknown }).error)
        : `${res.status} ${res.statusText}`;
    const err = new Error(`API request failed: ${message}`);
    (err as Error & { status?: number }).status = res.status;
    throw err;
  }
  return data as T;
}

// ─── Library endpoints ────────────────────────────────────────────────────

function userVideoFromApi(v: ApiUserVideo): UserVideo {
  return {
    youtubeVideoId: v.youtubeVideoId,
    title: v.title,
    channel: v.channel,
    thumbnailUrl: v.thumbnailUrl,
    status: v.status,
    savedAt: v.savedAt,
    skillCategory: v.skillCategory ?? null,
    difficulty: v.difficulty ?? null,
    techniques: v.techniques ?? [],
  };
}

export async function listVideos(token: string): Promise<UserVideo[]> {
  const data = await apiRequest<{ videos: ApiUserVideo[] }>("/videos", token);
  return data.videos.map(userVideoFromApi);
}

export async function bookmarkVideo(
  youtubeVideoId: string,
  token: string,
): Promise<UserVideo> {
  const data = await apiRequest<{ video: ApiUserVideo }>("/videos", token, {
    method: "POST",
    body: { youtubeVideoId },
  });
  return userVideoFromApi(data.video);
}

export async function deleteVideo(
  youtubeVideoId: string,
  token: string,
): Promise<void> {
  await apiRequest<void>(`/videos/${youtubeVideoId}`, token, {
    method: "DELETE",
  });
}

// ─── YouTube search endpoint ───────────────────────────────────────────────

export async function searchVideos(
  query: string,
  token: string,
): Promise<YouTubeSearchResult[]> {
  const params = new URLSearchParams({ q: query });
  const data = await apiRequest<{ results: YouTubeSearchResult[] }>(
    `/search?${params.toString()}`,
    token,
  );
  return data.results;
}

// ─── User profile endpoint ─────────────────────────────────────────────────

export async function getCurrentUser(
  token: string,
): Promise<CurrentUserProfile> {
  return apiRequest<CurrentUserProfile>("/users/me", token);
}

// ─── Transcription endpoint ────────────────────────────────────────────────

export async function transcribeVideo(
  youtubeVideoId: string,
  token: string,
): Promise<ApiVideoContent> {
  const data = await apiRequest<TranscribeResponse>(
    `/videos/${youtubeVideoId}/transcribe`,
    token,
    { method: "POST" },
  );
  return data.video;
}

// ─── Detail endpoint ───────────────────────────────────────────────────────

export async function getVideoDetail(
  youtubeVideoId: string,
  token: string,
): Promise<ApiDetailResponse | null> {
  try {
    return await apiRequest<ApiDetailResponse>(
      `/videos/${youtubeVideoId}`,
      token,
    );
  } catch (e) {
    if ((e as { status?: number }).status === 404) return null;
    throw e;
  }
}

export async function pollUntilReady(
  youtubeVideoId: string,
  token: string,
  options: { intervalMs?: number; timeoutMs?: number } = {},
): Promise<ApiDetailResponse> {
  const intervalMs = options.intervalMs ?? 3000;
  const timeoutMs = options.timeoutMs ?? 180000;
  const started = Date.now();

  while (Date.now() - started < timeoutMs) {
    const detail = await getVideoDetail(youtubeVideoId, token);
    if (detail?.content?.status === "ready") return detail;
    await new Promise((resolve) => setTimeout(resolve, intervalMs));
  }
  throw new Error("Timed out waiting for transcription to finish.");
}

// ─── Objective endpoints ──────────────────────────────────────────────────

export async function addObjective(
  youtubeVideoId: string,
  objective: { title: string; detail: string; timestampSec: number | null },
  token: string,
): Promise<ApiObjective> {
  const data = await apiRequest<{ objective: ApiObjective }>(
    `/videos/${youtubeVideoId}/objectives`,
    token,
    { method: "POST", body: objective },
  );
  return data.objective;
}

export interface ObjectivePatch {
  title?: string;
  detail?: string;
  timestampSec?: number | null;
  hidden?: boolean;
  checked?: boolean;
  notes?: string;
}

export async function patchObjective(
  youtubeVideoId: string,
  objectiveId: string,
  patch: ObjectivePatch,
  token: string,
): Promise<ApiDetailResponse> {
  return apiRequest<ApiDetailResponse>(
    `/videos/${youtubeVideoId}/objectives/${objectiveId}`,
    token,
    { method: "PATCH", body: patch },
  );
}

export async function deleteObjective(
  youtubeVideoId: string,
  objectiveId: string,
  token: string,
): Promise<void> {
  await apiRequest<void>(
    `/videos/${youtubeVideoId}/objectives/${objectiveId}`,
    token,
    { method: "DELETE" },
  );
}

// ─── Bookmark endpoints ───────────────────────────────────────────────────

export interface BookmarkPatch {
  timestampSec?: number;
  label?: string;
}

export async function addBookmark(
  youtubeVideoId: string,
  bookmark: { timestampSec: number; label: string },
  token: string,
): Promise<ApiBookmark> {
  const data = await apiRequest<{ bookmark: ApiBookmark }>(
    `/videos/${youtubeVideoId}/bookmarks`,
    token,
    { method: "POST", body: bookmark },
  );
  return data.bookmark;
}

export async function patchBookmark(
  youtubeVideoId: string,
  bookmarkId: string,
  patch: BookmarkPatch,
  token: string,
): Promise<ApiBookmark> {
  const data = await apiRequest<{ bookmark: ApiBookmark }>(
    `/videos/${youtubeVideoId}/bookmarks/${bookmarkId}`,
    token,
    { method: "PATCH", body: patch },
  );
  return data.bookmark;
}

export async function deleteBookmark(
  youtubeVideoId: string,
  bookmarkId: string,
  token: string,
): Promise<void> {
  await apiRequest<void>(
    `/videos/${youtubeVideoId}/bookmarks/${bookmarkId}`,
    token,
    { method: "DELETE" },
  );
}

// ─── Helpers to lift API objective shape into local-store shapes ──────────

export function toVideoObjectives(
  youtubeVideoId: string,
  content: ApiVideoContent | null,
  objectives: ApiObjective[],
): VideoObjectives | null {
  const ready = content?.status === "ready";
  if (!ready && objectives.length === 0) return null;
  return {
    youtubeVideoId,
    summary: content?.summary ?? "",
    instrument: content?.instrument ?? "unknown",
    skillCategory: content?.skillCategory ?? "Technique",
    transcriptSource: content?.transcriptSource ?? null,
    objectives: objectives.map(({ isCustom: _isCustom, ...rest }) => rest),
  };
}
